import type { SimpleStreamOptions } from "@earendil-works/pi-ai";
import type { ProviderConfig, ThinkingLevel } from "@office-agents/core";
import {
  getChattyApiBaseUrl,
  getStoredOrg,
  getValidJwt,
  normalizeChattyUrl,
} from "../auth/chatty-auth";

const CHATTY_SESSION_KEY = "chatty-office-client-session-id";
export const CHATTY_THINKING_LEVEL_KEY = "chatty-thinking-level";

export const CHATTY_GATEWAY_MODE =
  import.meta.env.VITE_CHATTY_GATEWAY_MODE === "true";

export const CHATTY_DEBUG_UI = import.meta.env.VITE_CHATTY_DEBUG_UI === "true";

export function getChattyGatewayUrl(): string {
  return normalizeChattyUrl(
    import.meta.env.VITE_CHATTY_GATEWAY_URL || `${getChattyApiBaseUrl()}/proxy`,
  );
}

export function getChattyModelSlug(): string {
  return import.meta.env.VITE_CHATTY_MODEL_SLUG || "chatty-smart-2";
}

export function getChattyWebSearchUrl(): string {
  return normalizeChattyUrl(`${getChattyApiBaseUrl()}/web/search`);
}

export function normalizeChattyThinkingLevel(level: unknown): ThinkingLevel {
  return ["low", "medium", "high"].includes(String(level))
    ? (String(level) as ThinkingLevel)
    : "medium";
}

export function getChattyThinkingLevel(): ThinkingLevel {
  const stored = localStorage.getItem(CHATTY_THINKING_LEVEL_KEY);
  const level =
    stored || import.meta.env.VITE_CHATTY_THINKING_LEVEL || "medium";
  return normalizeChattyThinkingLevel(level);
}

export function setChattyThinkingLevel(level: ThinkingLevel): ThinkingLevel {
  const normalized = normalizeChattyThinkingLevel(level);
  localStorage.setItem(CHATTY_THINKING_LEVEL_KEY, normalized);
  return normalized;
}

export function getChattyTenantId(): string | null {
  return getStoredOrg()?.id ?? null;
}

export function getOrCreateChattySessionId(): string {
  const existing = localStorage.getItem(CHATTY_SESSION_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem(CHATTY_SESSION_KEY, id);
  return id;
}

export function createChattyTraceId(): string {
  return crypto.randomUUID();
}

export function createChattyProviderConfig(): ProviderConfig {
  return {
    provider: "custom",
    apiKey: "chatty-gateway",
    model: getChattyModelSlug(),
    useProxy: false,
    proxyUrl: "",
    thinking: getChattyThinkingLevel(),
    followMode: true,
    expandToolCalls: false,
    apiType: "openai-completions",
    customBaseUrl: getChattyGatewayUrl(),
    authMethod: "apikey",
  };
}

export interface ChattyStreamOptionsConfig {
  pluginName?: string;
  modelSlug?: string;
  thinkingLevel?: ThinkingLevel;
}

export async function createChattyWebSearchHeaders(): Promise<
  Record<string, string>
> {
  const jwt = await getValidJwt();
  return jwt ? { Authorization: `Bearer ${jwt}` } : {};
}

export async function createChattyStreamOptions(
  config: ChattyStreamOptionsConfig = {},
): Promise<Partial<SimpleStreamOptions>> {
  const jwt = await getValidJwt();
  const tenantId = getChattyTenantId();
  const sessionId = getOrCreateChattySessionId();

  const headers: Record<string, string> = {
    "X-Chatty-Plugin": config.pluginName ?? "office",
    "X-Chatty-Model": config.modelSlug ?? getChattyModelSlug(),
    "X-Chatty-Thinking-Level": normalizeChattyThinkingLevel(
      config.thinkingLevel ?? getChattyThinkingLevel(),
    ),
    "X-Chatty-Pi-Parity": "true",
    "X-Chatty-Session-Id": sessionId,
    "X-Chatty-Trace-Id": createChattyTraceId(),
  };

  if (jwt) headers.Authorization = `Bearer ${jwt}`;
  if (tenantId) headers["X-Chatty-Tenant-Id"] = tenantId;

  return { headers };
}
